import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccessPermissionInterface } from '@thro-types/access-handler';

import { AxiosRequestConfig } from 'axios';

import {
  createAccessPermission,
  deleteAccessPermission,
  getAccessPermissions,
  updateAccessPermission
} from 'api/accessHandlerService';

export const useAccessPermissionsQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['accessPermissions', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const accessPermissions = getAccessPermissions(config);
      return accessPermissions;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useAccessPermissionsMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['accessPermissions'];

  const create = useMutation<AccessPermissionInterface, Error, { name: string }>({
    mutationFn: async args => {
      return await createAccessPermission(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<
    AccessPermissionInterface,
    Error,
    { id: number; data: { name: string } }
  >({
    mutationFn: async args => {
      return await updateAccessPermission(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<AccessPermissionInterface, Error, number>({
    mutationFn: async args => {
      return await deleteAccessPermission(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
